import { format } from "date-fns";
import { TIMELIST, getTimeRange } from "components/date-picker/utils";

// H2H per-area distribution scheduling (F6). A location card holds a nested
// model — one date, each with its own set of distribution time slots:
//
//   schedule: [{ date: "2026-12-14", times: [7, 8] }, …]
//
// The pathfinder address schema only carries two scalar fields (`startDate`,
// `time`), so the nested model is flattened into the legacy `#`-delimited
// parallel-string encoding on save (one (date,time) pair per index) and
// regrouped by date on load. This keeps the existing per-(date,time) distributor
// /flyer calculation intact (epic T9) — no API/schema change.

export interface AreaScheduleEntry {
  /** Calendar day, "yyyy-MM-dd" (timezone-safe, no time component). */
  date: string;
  /** Distribution time-slot codes (TIMELIST hours), ascending, unique. */
  times: number[];
}

export interface AreaTotals {
  distributors: number;
  flyers: number;
}

const SEP = "#";
// Placeholder time token for a date that has no slots yet. Real slots are
// TIMELIST hours (7-16), so "0" never collides. It lets a just-added date row
// survive a save/reload (the schema pairs each date with exactly one time, so a
// date with zero times would otherwise serialize to nothing and be lost). Note:
// when the H2H quote/cost calc is wired later, it must skip "0" time tokens —
// they're date-only placeholders, not real distribution slots.
const EMPTY_TIME = "0";
const pad2 = (n: number) => (n < 10 ? "0" : "") + n;

// Selectable distribution time slots — each a 3-hour window (e.g. "7 AM - 10 AM").
export const DISTRIBUTION_TIME_OPTIONS: { value: number; label: string }[] = TIMELIST.map((value) => ({
  value,
  label: getTimeRange(value),
}));

export const timeSlotLabel = (value: number): string => getTimeRange(value);

// Calendar-day <-> string. Local Y-M-D so the chosen day never shifts across
// timezones (mirrors launchDate's serialization rationale).
export const toScheduleDate = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// Returns null for an unparseable value (corrupt/legacy persisted data) so
// every consumer handles the invalid case explicitly instead of receiving an
// Invalid Date that poisons comparisons, sorts and date-fns calls downstream.
export const parseScheduleDate = (value: string): Date | null => {
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatScheduleDate = (value: string): string => {
  const date = parseScheduleDate(value);
  return date ? format(date, "MMM d, yyyy") : "";
};

export const sameScheduleDate = (a: Date, value: string): boolean => toScheduleDate(a) === value;

// AC35: a schedule holds a date earlier than the earliest still-valid day (today
// + lead time). Used to flag a re-entered draft whose distribution dates have
// since fallen in the past / within the lead time. An unparseable date counts
// as outdated — it can't be a valid future day, and flagging it keeps the
// submit gate closed until the row is fixed/removed.
export const scheduleHasOutdatedDate = (schedule: AreaScheduleEntry[], earliest: Date): boolean =>
  schedule.some((entry) => {
    const date = parseScheduleDate(entry.date);
    return date === null || date < earliest;
  });

/** Total (date, time) combinations across the schedule — the mission-pair count. */
export const countPairs = (schedule: AreaScheduleEntry[]): number =>
  schedule.reduce((sum, entry) => sum + entry.times.length, 0);

// Consolidated per-area totals (AC27): distributors = pairs × BAs-per-location;
// flyers = distributors × flyer-pack. Same per-combination source as today (T9).
export const computeAreaTotals = (
  schedule: AreaScheduleEntry[],
  baPerLocation: number,
  flyerPack: number
): AreaTotals => {
  const distributors = countPairs(schedule) * baPerLocation;
  return { distributors, flyers: distributors * flyerPack };
};

// Flatten the nested schedule into the parallel `#`-strings the address schema
// stores. Sorted by (date, time) so the encoding is stable regardless of the
// order the user picked dates/times in. A date with no times yet emits a single
// EMPTY_TIME pair so the row persists (and isn't lost on reload).
export const packSchedule = (schedule: AreaScheduleEntry[]): { startDate: string; time: string } => {
  const pairs: { date: string; time: string }[] = [];
  schedule.forEach((entry) => {
    if (entry.times.length === 0) {
      pairs.push({ date: entry.date, time: EMPTY_TIME });
      return;
    }
    [...entry.times].sort((a, b) => a - b).forEach((time) => pairs.push({ date: entry.date, time: String(time) }));
  });
  pairs.sort((a, b) => (a.date === b.date ? Number(a.time) - Number(b.time) : a.date < b.date ? -1 : 1));
  return {
    startDate: pairs.map((p) => p.date).join(SEP),
    time: pairs.map((p) => p.time).join(SEP),
  };
};

// Regroup the parallel `#`-strings back into the nested schedule. Mismatched
// lengths (corrupt/partial data) yield an empty schedule rather than silently
// pairing the wrong date with the wrong time. An EMPTY_TIME ("0") token restores
// a date row with no slots; real slots (7-16) are added to their date.
export const unpackSchedule = (startDate?: string | null, time?: string | null): AreaScheduleEntry[] => {
  const dates = (startDate ?? "").split(SEP).filter(Boolean);
  const times = (time ?? "").split(SEP).filter(Boolean);
  if (dates.length === 0 || dates.length !== times.length) return [];

  const byDate = new Map<string, number[]>();
  dates.forEach((date, i) => {
    const slots = byDate.get(date) ?? [];
    byDate.set(date, slots); // ensure the date row exists even with no times
    const slot = Number(times[i]);
    if (Number.isFinite(slot) && slot !== 0 && !slots.includes(slot)) slots.push(slot);
  });

  return [...byDate.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, slots]) => ({ date, times: slots.sort((a, b) => a - b) }));
};

// Toggle a calendar day in the schedule: add an empty-times row if absent,
// remove its row (and chips) if present. Drives the inline calendar (AC28).
export const toggleScheduleDate = (schedule: AreaScheduleEntry[], date: string): AreaScheduleEntry[] => {
  if (schedule.some((e) => e.date === date)) return schedule.filter((e) => e.date !== date);
  return [...schedule, { date, times: [] }].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

// Bulk-add calendar days (HUB-445 range/week/month selection): adds an
// empty-times row for every date not already scheduled, keeping the sort order.
// Existing rows (and their time slots) are left untouched.
export const addScheduleDates = (schedule: AreaScheduleEntry[], dates: string[]): AreaScheduleEntry[] => {
  const existing = new Set(schedule.map((e) => e.date));
  const fresh = [...new Set(dates)].filter((date) => !existing.has(date));
  if (fresh.length === 0) return schedule;
  return [...schedule, ...fresh.map((date) => ({ date, times: [] }))].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0
  );
};

// Bulk-remove calendar days (HUB-445 week/month toggle-off).
export const removeScheduleDates = (schedule: AreaScheduleEntry[], dates: string[]): AreaScheduleEntry[] => {
  const drop = new Set(dates);
  return schedule.filter((e) => !drop.has(e.date));
};

// Toggle a single time slot within a date row (AC29 chip add/remove).
export const toggleScheduleTime = (schedule: AreaScheduleEntry[], date: string, slot: number): AreaScheduleEntry[] =>
  schedule.map((entry) => {
    if (entry.date !== date) return entry;
    const times = entry.times.includes(slot)
      ? entry.times.filter((t) => t !== slot)
      : [...entry.times, slot].sort((a, b) => a - b);
    return { ...entry, times };
  });

export const removeScheduleDate = (schedule: AreaScheduleEntry[], date: string): AreaScheduleEntry[] =>
  schedule.filter((e) => e.date !== date);
